<script setup>
import { reactive, watch, computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import agentsAPI from 'dashboard/api/agents';

const props = defineProps({
  settings: { type: Object, required: true },
  isSaving: { type: Boolean, default: false },
  isTesting: { type: Boolean, default: false },
  testingMode: { type: String, default: 'auto_reply' },
});

const emit = defineEmits(['save', 'test-connection']);

const { t } = useI18n();

const messageTypeOptions = [
  { value: 'text', label: 'AI_REPLY_SETTINGS.FORM.MESSAGE_TYPES.TEXT' },
  { value: 'email', label: 'AI_REPLY_SETTINGS.FORM.MESSAGE_TYPES.EMAIL' },
  { value: 'image', label: 'AI_REPLY_SETTINGS.FORM.MESSAGE_TYPES.IMAGE' },
  {
    value: 'attachment',
    label: 'AI_REPLY_SETTINGS.FORM.MESSAGE_TYPES.ATTACHMENT',
  },
];

const state = reactive({
  enabled: false,
  aiAgentId: null,
  autoReplyApiEndpoint: '',
  autoReplyApiKey: '',
  autoReplyLkeBotBizId: '',
  suggestionApiEndpoint: '',
  suggestionApiKey: '',
  suggestionLkeBotBizId: '',
  humanHandoffPromptEnabled: true,
  timeoutSeconds: 10,
  retryCount: 0,
  eligibleMessageTypes: [],
  autoReplyLastTestStatus: null,
  autoReplyLastTestedAt: null,
  suggestionLastTestStatus: null,
  suggestionLastTestedAt: null,
});

const availableAgents = ref([]);
const isCreatingAgent = ref(false);

const applySettings = settings => {
  state.enabled = settings.enabled ?? false;
  state.aiAgentId = settings.aiAgentId ?? null;
  state.autoReplyApiEndpoint = settings.autoReplyApiEndpoint ?? '';
  state.autoReplyApiKey = settings.autoReplyApiKey ?? '';
  state.autoReplyLkeBotBizId = settings.autoReplyLkeBotBizId ?? '';
  state.suggestionApiEndpoint = settings.suggestionApiEndpoint ?? '';
  state.suggestionApiKey = settings.suggestionApiKey ?? '';
  state.suggestionLkeBotBizId = settings.suggestionLkeBotBizId ?? '';
  state.humanHandoffPromptEnabled = settings.humanHandoffPromptEnabled ?? true;
  state.timeoutSeconds = settings.timeoutSeconds ?? 10;
  state.retryCount = settings.retryCount ?? 0;
  state.eligibleMessageTypes = settings.eligibleMessageTypes
    ? [...settings.eligibleMessageTypes]
    : [];
  state.autoReplyLastTestStatus = settings.autoReplyLastTestStatus ?? null;
  state.autoReplyLastTestedAt = settings.autoReplyLastTestedAt ?? null;
  state.suggestionLastTestStatus = settings.suggestionLastTestStatus ?? null;
  state.suggestionLastTestedAt = settings.suggestionLastTestedAt ?? null;
};

watch(() => props.settings, applySettings, { immediate: true, deep: true });

const statusKeyFor = status => {
  if (!status) return '';
  return status === 'success'
    ? 'AI_REPLY_SETTINGS.FORM.STATUS.SUCCESS'
    : 'AI_REPLY_SETTINGS.FORM.STATUS.FAILED';
};

const autoReplyStatusLabel = computed(() =>
  statusKeyFor(state.autoReplyLastTestStatus)
);
const suggestionStatusLabel = computed(() =>
  statusKeyFor(state.suggestionLastTestStatus)
);

const toggleMessageType = value => {
  if (state.eligibleMessageTypes.includes(value)) {
    state.eligibleMessageTypes = state.eligibleMessageTypes.filter(
      type => type !== value
    );
    return;
  }
  state.eligibleMessageTypes = [...state.eligibleMessageTypes, value];
};

const buildPayload = () => ({
  enabled: state.enabled,
  aiAgentId: state.aiAgentId,
  autoReplyApiEndpoint: state.autoReplyApiEndpoint,
  autoReplyApiKey: state.autoReplyApiKey,
  autoReplyLkeBotBizId: state.autoReplyLkeBotBizId,
  suggestionApiEndpoint: state.suggestionApiEndpoint,
  suggestionApiKey: state.suggestionApiKey,
  suggestionLkeBotBizId: state.suggestionLkeBotBizId,
  humanHandoffPromptEnabled: state.humanHandoffPromptEnabled,
  timeoutSeconds: state.timeoutSeconds,
  retryCount: state.retryCount,
  eligibleMessageTypes: state.eligibleMessageTypes,
  autoReplyLastTestStatus: state.autoReplyLastTestStatus,
  autoReplyLastTestedAt: state.autoReplyLastTestedAt,
  suggestionLastTestStatus: state.suggestionLastTestStatus,
  suggestionLastTestedAt: state.suggestionLastTestedAt,
});

const handleSave = () => {
  emit('save', buildPayload());
};

const handleTest = mode => {
  emit('test-connection', mode);
};

const loadAvailableAgents = async () => {
  try {
    const response = await agentsAPI.get();
    availableAgents.value = response.data || [];
  } catch (error) {
    console.error('Failed to load agents:', error);
  }
};

const handleCreateAiAgent = async () => {
  isCreatingAgent.value = true;
  try {
    const response = await agentsAPI.createAiAgent();
    const newAgent = response.data;
    availableAgents.value.push(newAgent);
    state.aiAgentId = newAgent.id;
    useAlert(t('AI_REPLY_SETTINGS.MESSAGES.AI_AGENT_CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('AI_REPLY_SETTINGS.MESSAGES.AI_AGENT_CREATE_ERROR'));
  } finally {
    isCreatingAgent.value = false;
  }
};

onMounted(() => {
  loadAvailableAgents();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <SettingsSection :show-border="false">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ $t('AI_REPLY_SETTINGS.FORM.ENABLED.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('AI_REPLY_SETTINGS.FORM.ENABLED.DESCRIPTION') }}
          </p>
        </div>
        <label class="relative inline-flex items-center cursor-pointer">
          <input
            v-model="state.enabled"
            data-test-id="ai-reply-enabled-toggle"
            type="checkbox"
            class="sr-only peer"
          />
          <div
            class="w-11 h-6 bg-n-alpha-black2 rounded-full peer peer-checked:bg-n-brand transition"
          />
          <div
            class="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"
          />
        </label>
      </div>
    </SettingsSection>

    <SettingsSection
      v-if="state.enabled"
      :title="$t('AI_REPLY_SETTINGS.FORM.AI_AGENT.TITLE')"
      :sub-title="$t('AI_REPLY_SETTINGS.FORM.AI_AGENT.HELP_TEXT')"
    >
      <div class="flex flex-col gap-3">
        <select
          v-model="state.aiAgentId"
          data-test-id="ai-reply-agent-select"
          class="h-10 rounded-lg border border-n-weak px-3 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand disabled:opacity-60"
          :disabled="isCreatingAgent"
        >
          <option :value="null">
            {{ $t('AI_REPLY_SETTINGS.FORM.AI_AGENT.SELECT_PLACEHOLDER') }}
          </option>
          <option
            v-for="agent in availableAgents"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }} ({{ agent.email }})
          </option>
        </select>

        <button
          type="button"
          class="reset-base inline-flex h-10 items-center justify-center rounded-lg bg-n-brand px-4 text-sm font-medium text-white transition hover:bg-n-brand/90 disabled:opacity-60"
          data-test-id="ai-reply-create-agent"
          :disabled="isCreatingAgent"
          @click="handleCreateAiAgent"
        >
          {{
            isCreatingAgent
              ? $t('AI_REPLY_SETTINGS.FORM.AI_AGENT.CREATING')
              : $t('AI_REPLY_SETTINGS.FORM.AI_AGENT.CREATE_NEW')
          }}
        </button>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.AUTO_REPLY_TITLE')"
    >
      <div class="grid gap-3">
        <Input
          v-model="state.autoReplyApiEndpoint"
          data-test-id="ai-reply-auto-reply-api-endpoint"
          :label="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.API_ENDPOINT')"
          :placeholder="
            $t('AI_REPLY_SETTINGS.FORM.CONNECTION.API_ENDPOINT_HINT')
          "
        />
        <Input
          v-model="state.autoReplyApiKey"
          data-test-id="ai-reply-auto-reply-api-key"
          type="password"
          :label="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.API_KEY')"
          :placeholder="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.API_KEY_HINT')"
        />
        <Input
          v-model="state.autoReplyLkeBotBizId"
          data-test-id="ai-reply-auto-reply-lke-bot-biz-id"
          :label="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.LKE_BOT_BIZ_ID')"
          :placeholder="
            $t('AI_REPLY_SETTINGS.FORM.CONNECTION.LKE_BOT_BIZ_ID_HINT')
          "
        />
      </div>

      <div class="mt-4 flex flex-col gap-2 text-sm text-n-slate-11">
        <div data-test-id="ai-reply-auto-reply-last-test-status">
          <span class="font-medium text-n-slate-12">
            {{ $t('AI_REPLY_SETTINGS.FORM.STATUS.LABEL') }}
          </span>
          <span v-if="autoReplyStatusLabel" class="ml-2">
            {{ $t(autoReplyStatusLabel) }}
          </span>
          <span v-else class="ml-2">{{
            $t('AI_REPLY_SETTINGS.FORM.STATUS.NONE')
          }}</span>
        </div>
        <div v-if="state.autoReplyLastTestedAt">
          <span class="font-medium text-n-slate-12">
            {{ $t('AI_REPLY_SETTINGS.FORM.STATUS.LAST_TESTED') }}
          </span>
          <span class="ml-2">{{ state.autoReplyLastTestedAt }}</span>
        </div>
      </div>

      <div class="mt-3 flex flex-wrap gap-2">
        <button
          class="reset-base inline-flex items-center justify-center rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 transition hover:border-n-slate-6 disabled:opacity-60"
          data-test-id="ai-reply-auto-reply-test-connection"
          :disabled="isTesting && testingMode === 'auto_reply'"
          @click="handleTest('auto_reply')"
        >
          {{ $t('AI_REPLY_SETTINGS.FORM.ACTIONS.TEST_CONNECTION') }}
        </button>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.SUGGESTION_TITLE')"
    >
      <div class="grid gap-3">
        <Input
          v-model="state.suggestionApiEndpoint"
          data-test-id="ai-reply-suggestion-api-endpoint"
          :label="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.API_ENDPOINT')"
          :placeholder="
            $t('AI_REPLY_SETTINGS.FORM.CONNECTION.API_ENDPOINT_HINT')
          "
        />
        <Input
          v-model="state.suggestionApiKey"
          data-test-id="ai-reply-suggestion-api-key"
          type="password"
          :label="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.API_KEY')"
          :placeholder="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.API_KEY_HINT')"
        />
        <Input
          v-model="state.suggestionLkeBotBizId"
          data-test-id="ai-reply-suggestion-lke-bot-biz-id"
          :label="$t('AI_REPLY_SETTINGS.FORM.CONNECTION.LKE_BOT_BIZ_ID')"
          :placeholder="
            $t('AI_REPLY_SETTINGS.FORM.CONNECTION.LKE_BOT_BIZ_ID_HINT')
          "
        />
      </div>

      <div class="mt-4 flex flex-col gap-2 text-sm text-n-slate-11">
        <div data-test-id="ai-reply-suggestion-last-test-status">
          <span class="font-medium text-n-slate-12">
            {{ $t('AI_REPLY_SETTINGS.FORM.STATUS.LABEL') }}
          </span>
          <span v-if="suggestionStatusLabel" class="ml-2">
            {{ $t(suggestionStatusLabel) }}
          </span>
          <span v-else class="ml-2">{{
            $t('AI_REPLY_SETTINGS.FORM.STATUS.NONE')
          }}</span>
        </div>
        <div v-if="state.suggestionLastTestedAt">
          <span class="font-medium text-n-slate-12">
            {{ $t('AI_REPLY_SETTINGS.FORM.STATUS.LAST_TESTED') }}
          </span>
          <span class="ml-2">{{ state.suggestionLastTestedAt }}</span>
        </div>
      </div>

      <div class="mt-3 flex flex-wrap gap-2">
        <button
          class="reset-base inline-flex items-center justify-center rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 transition hover:border-n-slate-6 disabled:opacity-60"
          data-test-id="ai-reply-suggestion-test-connection"
          :disabled="isTesting && testingMode === 'suggestion'"
          @click="handleTest('suggestion')"
        >
          {{ $t('AI_REPLY_SETTINGS.FORM.ACTIONS.TEST_CONNECTION') }}
        </button>
      </div>
    </SettingsSection>

    <SettingsSection :title="$t('AI_REPLY_SETTINGS.FORM.TUNING.TITLE')">
      <div class="grid gap-3">
        <div class="flex items-center justify-between gap-4 rounded-lg border border-n-weak px-4 py-3">
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AI_REPLY_SETTINGS.FORM.HUMAN_HANDOFF_PROMPT.TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{
                $t('AI_REPLY_SETTINGS.FORM.HUMAN_HANDOFF_PROMPT.DESCRIPTION')
              }}
            </span>
          </div>
          <label class="relative inline-flex items-center cursor-pointer">
            <input
              v-model="state.humanHandoffPromptEnabled"
              data-test-id="ai-reply-human-handoff-prompt-toggle"
              type="checkbox"
              class="sr-only peer"
            />
            <div
              class="w-11 h-6 bg-n-alpha-black2 rounded-full peer peer-checked:bg-n-brand transition"
            />
            <div
              class="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"
            />
          </label>
        </div>

        <Input
          v-model="state.timeoutSeconds"
          data-test-id="ai-reply-timeout-seconds"
          type="number"
          min="1"
          :label="$t('AI_REPLY_SETTINGS.FORM.TUNING.TIMEOUT')"
        />
        <Input
          v-model="state.retryCount"
          data-test-id="ai-reply-retry-count"
          type="number"
          min="0"
          :label="$t('AI_REPLY_SETTINGS.FORM.TUNING.RETRY')"
        />
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('AI_REPLY_SETTINGS.FORM.MESSAGE_TYPES.TITLE')"
      :sub-title="$t('AI_REPLY_SETTINGS.FORM.MESSAGE_TYPES.DESCRIPTION')"
    >
      <div
        class="grid gap-3 sm:grid-cols-2"
        data-test-id="ai-reply-eligible-message-types"
      >
        <label
          v-for="option in messageTypeOptions"
          :key="option.value"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <input
            type="checkbox"
            class="h-4 w-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
            :value="option.value"
            :checked="state.eligibleMessageTypes.includes(option.value)"
            @change="toggleMessageType(option.value)"
          />
          <span>{{ $t(option.label) }}</span>
        </label>
      </div>
    </SettingsSection>

    <SettingsSection :title="$t('AI_REPLY_SETTINGS.FORM.ACTIONS.TITLE')">
      <div class="flex flex-wrap gap-2">
        <button
          class="reset-base inline-flex items-center justify-center rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition hover:bg-n-brand/90 disabled:opacity-60"
          data-test-id="ai-reply-save"
          :disabled="isSaving"
          @click="handleSave"
        >
          {{ $t('AI_REPLY_SETTINGS.FORM.ACTIONS.SAVE') }}
        </button>
      </div>
    </SettingsSection>
  </div>
</template>
